import cn from 'classnames';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { getModuleFrames } from 'src/helpers/getModuleFrames';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { usePostMesssage } from 'src/hooks/use-postMessage';
import { getFooterConfigs } from 'src/services/configsApi';
import PersisterService from 'src/services/PersisterService';
import { setGeneraLoading } from 'src/store/app/actions';
import { RootState } from 'src/types/store-types';
import { IFRAMES } from 'src/utils/constants';
import { Constructor } from './constructor';
import './styles.scss';

const DesktopFooterConstructor: FC = () => {
  const { t }: Translation = useTranslation();

  const _setGeneraLoading = useConnectedAction(setGeneraLoading);

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { user } = useSelector((state: RootState) => state.user);
  const { navigation, generalConfigs } = useSelector((state: RootState) => state.configs);

  const footerViewWithBtn = useMemo(() => {
    return [...IFRAMES, ...getModuleFrames()].includes(navigation.filter((f) => f.name === selectedCategory)[0]?.type);
  }, [selectedCategory, navigation, generalConfigs]);

  const [open, setOpen] = useState<boolean>(false);
  const [configs, setConfigs] = useState<Settings>({
    template: '',
    sections: [],
    styles: {},
  });

  const sectionsArr = Object.values(configs?.sections as ISection[]);

  const footerComponent = useMemo(
    () => (
      <>
        {sectionsArr?.map((obj: any, i: number) => {
          return <React.Fragment key={obj + i}>{Constructor.renderWidgets(obj)}</React.Fragment>;
        })}
      </>
    ),
    [sectionsArr]
  );

  const getFooterConf = async (): Promise<void> => {
    try {
      if (PersisterService.hasPersistValue('desktopFooter')) {
        const res = PersisterService.getPersistValue('desktopFooter');
        setConfigs(res);
      } else {
        const res = await getFooterConfigs('desktop');

        if (res.success) {
          _setGeneraLoading(true);
          setConfigs(res.result);
          PersisterService.setPersistValue('desktopFooter', res.result);
        } else {
          _setGeneraLoading(false);
        }
      }
    } catch (e) {
      _setGeneraLoading(false);
      console.log(e);
    }
  };

  useEffect(() => {
    getFooterConf();
  }, [user]);

  usePostMesssage((v: any) => setConfigs(v), 'desktopFooter');

  if (sectionsArr.length > 0) {
    return (
      <>
        {footerViewWithBtn ? (
          <div className="footer_outer_container">
            <div className={cn('footer_with_btn', { ['open']: open })} style={configs?.styles}>
              <Button
                onClick={() => setOpen((prev) => !prev)}
                ariaLabelledby={open ? t('close') : t('show_more')}
                className={cn('footer_icon_btn', { ['rotateArrow']: open })}
              >
                <SvgIcon src={open ? icons.close : icons.arrowDown} />
              </Button>
              <div className={cn('footer_container_with_btn', { ['show']: open })}>
                <div style={configs?.styles} className={cn('footer_container_inner', configs?.template)}>
                  {configs?.sections && footerComponent}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="footer_outer_container">
            <div style={configs.styles} className={cn('footer_container', configs?.template)}>
              {configs?.sections && footerComponent}
            </div>
          </div>
        )}
      </>
    );
  }
  return <></>;
};
export default DesktopFooterConstructor;
